import "./pricingTable.css";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import { Reveal } from "../utils/Reveal";
import { useState } from "react";
import { UserAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

type PricingTableProps = {
  heading: string;
  paragraph: string;
  annualSaving: number;
  heading2: string;
  paragraph2: string;
  cta: string;
  cards: CardType[];
};

export type CardType = {
  cardHeading: string;
  cardMonthlyPricing: number;
  cardAnnualPricing: number;
  cardPoints: string[];
  cardCta: string;
  cardId: string | undefined;
  cardRecommmended: boolean;
  cardImg: any;
};

const PricingTable = (props: PricingTableProps) => {
  const { user, roles } = UserAuth();
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState<boolean>(false);

  const checkout = async (id: string) => {
    const product = {
      productId: id,
    };

    if (!user) {
      navigate("/signin");
      return;
    }

    if (!id) {
      return;
    } else if (roles.premium && id === process.env.REACT_APP_STRIPE_PRODUCT_1) {
      return;
    } else {
      try {
        const token = await user.getIdToken();

        await fetch(`${process.env.REACT_APP_API_URL}/checkout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(product),
        })
          .then((response) => {
            return response.json();
          })
          .then((response) => {
            if (response.data.sessionUrl) {
              window.location.assign(response.data.sessionUrl);
            }
          });
      } catch (error) {
        console.log("Error Fetching!");
      }
    }
  };

  return (
    <div
      className="pricingTable"
      id="pricing"
      role="region"
      aria-labelledby="pricing-heading"
    >
      <div className="pricingTable__container">
        <div className="pricingTable__container__header">
          <Reveal>
            <>
              <h2 className="home-headers" id="pricing-heading">
                {props.heading}
              </h2>
              <p>{props.paragraph}</p>
            </>
          </Reveal>
        </div>

        <Form
          className="pricingTable__container__switch"
          role="group"
          aria-label="Billing cycle selection"
        >
          <p>Monthly</p>
          <Form.Check
            type="switch"
            id="pricing-switch"
            onClick={() => {
              setBillingCycle(!billingCycle);
            }}
            aria-label="Switch between monthly and annual billing"
          />
          <p>Annually</p>
        </Form>
        <p className="pricingTable__container__saving" role="status">
          Save {props.annualSaving}% with an annual subscription
        </p>

        <div className="pricingTable__card-group" role="list">
          {props.cards.map((card: CardType, i) => {
            return (
              <Card className="text-center" key={i} role="listitem">
                <Card.Header>
                  {card.cardHeading}
                  {card.cardRecommmended && (
                    <div className="card__recomended">Recommended</div>
                  )}
                  <img
                    src={card.cardImg}
                    alt={`${card.cardHeading} plan illustration`}
                    className="card__image"
                  />
                </Card.Header>
                <Card.Body>
                  <Card.Title>
                    £
                    {billingCycle
                      ? card.cardAnnualPricing
                      : card.cardMonthlyPricing}
                    <small className="text-muted">
                      / {billingCycle ? "yearly" : "monthly"}
                    </small>
                  </Card.Title>
                  <div>
                    <ul className="list-unstyled mt-3 mb-4" role="list">
                      {card.cardPoints.map((point, i) => {
                        return (
                          <div key={i}>
                            <li className="star-bullet-point">{point}</li>
                            <div className="divider"></div>
                          </div>
                        );
                      })}
                    </ul>
                  </div>
                  <button
                    className="btn-cta"
                    onClick={() => checkout(card.cardId!)}
                  >
                    {card.cardCta}
                  </button>
                </Card.Body>
              </Card>
            );
          })}
        </div>

        <div className="pricingTable__container__footer">
          <Reveal>
            <>
              <h2>{props.heading2}</h2>
              <p>{props.paragraph2}</p>
              <span className="pricingTable__container__footer__cta">
                {props.cta}
              </span>
            </>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default PricingTable;
